import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { osFetch } from "../api";
import { useOS } from "../OSContext";
import { Boxes, Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const LIFECYCLES = ["idea", "incubating", "live", "sunset"];
const HEALTH_COLOR = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500", unknown: "bg-slate-600" };

export default function CatalogList() {
  const { can } = useOS();
  const [products, setProducts] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => osFetch("/catalog").then(setProducts).catch((e) => { setProducts([]); toast.error(e.message); });
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl" data-testid="os-catalog">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1">Product Catalog</p>
          <h1 className="font-display font-extrabold text-3xl">Products</h1>
        </div>
        {can("create") && (
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-3 rounded-2xl font-semibold hover:-translate-y-0.5 transition-transform duration-300" data-testid="os-catalog-new-btn">
            <Plus size={16} /> New product
          </button>
        )}
      </div>

      {products === null ? (
        <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-slate-500">No products yet. Create the first one.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Link key={p.id} to={`/os/catalog/${p.id}`} data-testid={`os-product-${p.id}`}
              className="group rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all duration-300">
              <div className="flex items-center gap-2">
                <Boxes size={18} className="text-[#00F0FF]" />
                <span className={`w-2 h-2 rounded-full ${HEALTH_COLOR[p.health] || HEALTH_COLOR.unknown}`} title={`health: ${p.health}`} />
                <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-slate-500 border border-white/10 rounded-full px-2 py-0.5">{p.lifecycle}</span>
              </div>
              <p className="font-display font-bold text-lg mt-3">{p.name}</p>
              <p className="text-slate-500 text-sm mt-1 line-clamp-2">{p.description || p.why || "—"}</p>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", why: "", lifecycle: "idea", north_star: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try { const p = await osFetch("/catalog", { method: "POST", body: form }); toast.success("Product created"); onCreated(p); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()} data-testid="os-catalog-create-modal">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display font-bold text-xl">New product</h2><button onClick={onClose}><X size={18} className="text-slate-500" /></button></div>
        <div className="space-y-3">
          <Field label="Name (what is it?)"><input value={form.name} onChange={set("name")} className="os-input" data-testid="os-catalog-name" autoFocus /></Field>
          <Field label="Description"><textarea value={form.description} onChange={set("description")} rows={2} className="os-input" /></Field>
          <Field label="Why does it exist?"><textarea value={form.why} onChange={set("why")} rows={2} className="os-input" data-testid="os-catalog-why" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lifecycle"><select value={form.lifecycle} onChange={set("lifecycle")} className="os-input">{LIFECYCLES.map((l) => <option key={l} value={l}>{l}</option>)}</select></Field>
            <Field label="North-star metric"><input value={form.north_star} onChange={set("north_star")} className="os-input" /></Field>
          </div>
        </div>
        <button onClick={submit} disabled={busy || !form.name.trim()} className="mt-5 w-full bg-[#0055FF] text-white py-3 rounded-xl font-semibold disabled:opacity-40" data-testid="os-catalog-create-submit">{busy ? "Creating…" : "Create product"}</button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span><div className="mt-1">{children}</div></label>
);
